/**
 * #1336 — Add Source modal.
 *
 * The backend has supported multi-source projects since #900, with
 * cross-repo analysis stitching frontend api-calls to backend routes
 * via per-source `baseUrl` boundaries (#906/#910). The UI lagged —
 * project creation only accepts one source, and the legacy
 * ProjectPage rendered the sources list read-only.
 *
 * This modal mirrors the DashboardPage create-project form for the
 * source-of-truth fields (label, role, sourceType, repoUrl OR
 * localPath) plus an optional baseUrl for the cross-repo stitcher.
 * Submit POSTs `/projects/:id/sources`; parent calls loadProject() on
 * success to repaint.
 */
import { useState } from 'react';
import { api } from '../../services/api';
import { GithubRepoPicker } from '../github/GithubRepoPicker';
import type { CreateSourceInput } from '@veodiagram/shared';

interface AddSourceModalProps {
  projectId: string;
  onClose: () => void;
  /** Fires after a successful POST so the parent can re-fetch the project. */
  onSuccess: () => void;
}

type SourceType = 'local' | 'github';
type SourceRole = 'frontend' | 'backend' | 'shared' | 'unknown';

const ROLE_DESCRIPTIONS: Record<SourceRole, string> = {
  frontend: 'Browser-side code that calls APIs.',
  backend: 'Server-side code that handles routes.',
  shared: 'Code shared by frontend + backend (e.g. monorepo packages).',
  unknown: 'Pick later — the analyzer will infer where possible.',
};

export function AddSourceModal({
  projectId,
  onClose,
  onSuccess,
}: AddSourceModalProps) {
  const [sourceType, setSourceType] = useState<SourceType>('local');
  const [label, setLabel] = useState('');
  const [role, setRole] = useState<SourceRole>('unknown');
  const [localPath, setLocalPath] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      setError('Label is required.');
      return;
    }
    if (sourceType === 'local' && !localPath.trim()) {
      setError('Local path is required.');
      return;
    }
    if (sourceType === 'github' && !repoUrl.trim()) {
      setError('Repository URL is required.');
      return;
    }
    const payload: CreateSourceInput = {
      label: trimmedLabel,
      role,
      sourceType,
      ...(sourceType === 'local'
        ? { localPath: localPath.trim() }
        : { repoUrl: repoUrl.trim() }),
      ...(baseUrl.trim() ? { baseUrl: baseUrl.trim() } : {}),
    };
    setSubmitting(true);
    try {
      await api.addSource(projectId, payload);
      onSuccess();
      onClose();
    } catch (err) {
      setError((err as Error).message ?? 'Failed to add source.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-[min(520px,100%)] max-h-[90vh] overflow-y-auto bg-background border border-border rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Add source to project"
      >
        <header className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-base font-semibold">Add source</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <form onSubmit={onSubmit} className="p-4 space-y-3">
          <p className="text-xs text-muted-foreground">
            A project can host multiple sources (e.g. a frontend repo + a
            backend repo). The cross-repo analyzer stitches calls across
            them via each source's <code>baseUrl</code>.
          </p>

          <div>
            <label className="block text-sm font-medium mb-1">Label</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. api-server"
              className="w-full px-3 py-1.5 rounded-md border border-border bg-background text-sm"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as SourceRole)}
              className="w-full px-3 py-1.5 rounded-md border border-border bg-background text-sm"
            >
              <option value="unknown">unknown</option>
              <option value="frontend">frontend</option>
              <option value="backend">backend</option>
              <option value="shared">shared</option>
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">
              {ROLE_DESCRIPTIONS[role]}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Source type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSourceType('local')}
                className={`flex-1 px-3 py-1.5 rounded-md border text-sm transition-colors ${
                  sourceType === 'local'
                    ? 'border-primary bg-primary/10 text-foreground font-medium'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                Local folder
              </button>
              <button
                type="button"
                onClick={() => setSourceType('github')}
                className={`flex-1 px-3 py-1.5 rounded-md border text-sm transition-colors ${
                  sourceType === 'github'
                    ? 'border-primary bg-primary/10 text-foreground font-medium'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                GitHub URL
              </button>
            </div>
          </div>

          {sourceType === 'local' ? (
            <div>
              <label className="block text-sm font-medium mb-1">Local path</label>
              <input
                type="text"
                value={localPath}
                onChange={(e) => setLocalPath(e.target.value)}
                placeholder="/Users/you/projects/backend"
                className="w-full px-3 py-1.5 rounded-md border border-border bg-background text-sm font-mono"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1">Repository URL</label>
              <GithubRepoPicker onPick={(url) => setRepoUrl(url)} />
              <input
                type="text"
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                placeholder="https://github.com/org/repo"
                className="w-full px-3 py-1.5 rounded-md border border-border bg-background text-sm"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">
              Base URL <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://api.example.com"
              className="w-full px-3 py-1.5 rounded-md border border-border bg-background text-sm font-mono"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              The cross-repo stitcher strips this prefix from this source's
              frontend api-calls so they match the backend source's routes.
              Leave blank when both sources speak relative URLs.
            </p>
          </div>

          {error && (
            <div className="text-sm text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded p-2">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-md border border-border bg-background text-sm hover:bg-muted"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
              disabled={submitting}
            >
              {submitting ? 'Adding…' : 'Add source'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
